import {
  useEffect,
  useLayoutEffect,
  useRef,
  useSyncExternalStore,
} from "react";
import { useLocation, useNavigationType } from "react-router-dom";
import { usePageHeaderContext } from "@/contexts/PageHeaderContext";
import { matchContext } from "./contexts";
import { mainNavLabel } from "./mainNav";
import { stripPanelParam } from "./paths";
import type { UpTarget } from "./types";

/**
 * Origin (CONTEXT.md): the page, with its state, from which the user entered a
 * context other than through its structural parent. Kept per context in
 * sessionStorage so it survives section changes, child pages, reloads and the
 * context switcher, but is not part of a shared link.
 */
const ORIGINS_KEY = "nav-origins";
const SCROLL_KEY = "nav-scroll";

/** Location state flag: keep the current context's origin (switchers). */
export const KEEP_ORIGIN_STATE = { navOrigin: "keep" } as const;
/** Location state flag: restore the saved scroll position (up link). */
export const RESTORE_SCROLL_STATE = { restoreScroll: true } as const;

export const MAIN_SCROLL_ID = "main-scroll";

type Origins = Record<string, UpTarget>;

const listeners = new Set<() => void>();
let originsCache: Origins | null = null;

function readOrigins(): Origins {
  if (originsCache) return originsCache;
  try {
    originsCache = JSON.parse(sessionStorage.getItem(ORIGINS_KEY) ?? "{}");
  } catch {
    originsCache = {};
  }
  return originsCache!;
}

function writeOrigin(contextKey: string, origin: UpTarget | null) {
  const next = { ...readOrigins() };
  if (origin) next[contextKey] = origin;
  else delete next[contextKey];
  originsCache = next;
  try {
    sessionStorage.setItem(ORIGINS_KEY, JSON.stringify(next));
  } catch {
    // Storage full or unavailable: the origin then lives for this page only.
  }
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The origin of a context visit, or null (falls back to the parent). */
export function useOrigin(contextKey: string | null): UpTarget | null {
  const origins = useSyncExternalStore(subscribe, readOrigins);
  return contextKey ? (origins[contextKey] ?? null) : null;
}

function readScrollPositions(): Record<string, number> {
  try {
    return JSON.parse(sessionStorage.getItem(SCROLL_KEY) ?? "{}");
  } catch {
    return {};
  }
}

function saveScrollPosition(url: string, top: number) {
  const positions = readScrollPositions();
  positions[url] = top;
  // Keep the map small.
  const keys = Object.keys(positions);
  if (keys.length > 50) delete positions[keys[0]];
  try {
    sessionStorage.setItem(SCROLL_KEY, JSON.stringify(positions));
  } catch {
    // ignore
  }
}

interface PageSnapshot {
  url: string;
  pathname: string;
  contextKey: string | null;
}

/**
 * Watches main-view navigation: records origins when the user enters a
 * context (B3, B3a), remembers scroll positions of pages and restores them on
 * back/forward and when returning via the up link.
 */
export function NavigationTracker() {
  const location = useLocation();
  const navigationType = useNavigationType();
  // While the effect below runs for a new location, the page header still
  // holds the title of the page the user just left: the new page publishes
  // its own title in a later render. That is the label of the origin.
  const { title: leavingTitle } = usePageHeaderContext();
  const url = stripPanelParam(location.pathname, location.search);
  const previous = useRef<PageSnapshot | null>(null);

  useEffect(() => {
    const current = matchContext(location.pathname);
    const prev = previous.current;
    const state = location.state as { navOrigin?: string } | null;

    if (
      prev &&
      prev.url !== url &&
      current &&
      prev.contextKey !== current.key &&
      navigationType !== "POP"
    ) {
      const parent = current.def.parent(current.params);
      if (state?.navOrigin === "keep") {
        const kept = prev.contextKey ? readOrigins()[prev.contextKey] : null;
        writeOrigin(current.key, kept ?? null);
      } else if (
        prev.pathname === parent.href ||
        // A create page replaced by the created record is no origin.
        prev.pathname.endsWith("/new")
      ) {
        // Entered through the structural parent: no origin.
        writeOrigin(current.key, null);
      } else {
        writeOrigin(current.key, {
          label: leavingTitle ?? mainNavLabel(prev.pathname) ?? "Back",
          href: prev.url,
        });
      }
    }

    previous.current = {
      url,
      pathname: location.pathname,
      contextKey: current?.key ?? null,
    };
    // Only react to actual navigations.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  useScrollMemory(url, navigationType, location.state);
  return null;
}

function useScrollMemory(url: string, navigationType: string, state: unknown) {
  // Save positions while scrolling.
  useEffect(() => {
    const scroller = document.getElementById(MAIN_SCROLL_ID);
    if (!scroller) return;
    let frame = 0;
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() =>
        saveScrollPosition(url, scroller.scrollTop),
      );
    };
    scroller.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      scroller.removeEventListener("scroll", onScroll);
    };
  }, [url]);

  // Restore on back/forward and when the up link asks for it; otherwise
  // start new pages at the top.
  useLayoutEffect(() => {
    const scroller = document.getElementById(MAIN_SCROLL_ID);
    if (!scroller) return;
    const wantsRestore =
      navigationType === "POP" ||
      (state as { restoreScroll?: boolean } | null)?.restoreScroll === true;
    const target = wantsRestore ? readScrollPositions()[url] : undefined;
    if (!target) {
      scroller.scrollTop = 0;
      return;
    }
    // Content may still be loading: retry until it is tall enough.
    let attempts = 0;
    let timer = 0;
    const tryRestore = () => {
      scroller.scrollTop = target;
      if (Math.abs(scroller.scrollTop - target) > 2 && attempts++ < 20) {
        timer = window.setTimeout(tryRestore, 100);
      }
    };
    tryRestore();
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);
}
